import { AfterViewInit, Component, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { GlobalConfiguration } from 'src/app/core/classes/GlobalConfiguration';
import { Helper } from 'src/app/shared/classes/Helper';
import { HitApi } from 'src/app/shared/classes/HitApi';
import { IntegrationStates } from 'src/app/shared/classes/IntegrationStates';
import { Widget } from 'src/app/shared/classes/Widget';
import { WidgetInjectedData } from 'src/app/shared/classes/WidgetInjectedData';
import { ButtonColorType } from 'src/app/shared/enums/ButtonColorType';
import { ButtonType } from 'src/app/shared/enums/ButtonType';
import { IconType } from 'src/app/shared/enums/IconType';
import { ModalType } from 'src/app/shared/enums/ModalType';
import { IIntegrationApiResponse } from 'src/app/shared/interfaces/api/portlets/IIntegrationApiResponse';
import { IIntegrationInput } from 'src/app/shared/interfaces/integration/IIntegrationInput';
import { IModalData } from 'src/app/shared/interfaces/modal/IModalData';
import { RedirectionHandleService } from 'src/app/shared/services/redirection-handle/redirection-handle.service';
import { SlackTestingModalComponent } from '../../../modal-templates/integration/slack-testing-modal/slack-testing-modal.component';
import { IButtonGeneratorInput } from './../../../../interfaces/button-generator/IButtonGeneratorInput';

@Component({
    selector: 'app-slack-integration',
    templateUrl: './slack-integration.component.html',
    styleUrls: ['./slack-integration.component.sass']
})
export class SlackIntegrationComponent implements OnInit, AfterViewInit {
    integrationInput: IIntegrationInput = null;
    widgetRef: Widget;
    isIntegrated = false;
    isConfigured: Boolean = false;

    constructor(
        widgetData: WidgetInjectedData,
        private redirectionHandleService: RedirectionHandleService
    ) {
        this.widgetRef = widgetData.widgetRef;
    }

    ngOnInit(): void {
        this.setUpBasics();
    }

    setUpBasics() {
        this.integrationInput = {
            integrationName: this.widgetRef.widgetData.widgetInfo.text,
            integrationCategory: 'Message Integration',
            imageUrl: 'assets/integrations/slack_2.png',
            buttons: [],
            imagePadding: 6
        };
    }

    generateButtons(response: IIntegrationApiResponse) {
        if (this.isIntegrated) {
            // Adding Play Button
            this.integrationInput.buttons.push({
                buttonName: 'Test',
                buttonIcon: {
                    type: IconType.FONTAWSOME,
                    class: 'fas fa-play'
                },
                hoverText: 'Test Slack',
                buttonType: ButtonType.CIRCLE,
                buttonColorType: ButtonColorType.SUCCESS,
                function: () => {
                    const modalArgs: IModalData = {
                        modalName: 'Slack Integration Test',
                        sourceId: Symbol(),
                        modalType: ModalType.MIDDLE,
                        extraStepContainerClass: 'remove-form-space',
                        modalIcon: {
                            type: IconType.FONTAWSOME,
                            class: 'fas fa-play'
                        },
                        modalSteps: [
                            {
                                stepName: 'Slack Integration Test',
                                resetModal: true,
                                stepData: {
                                    componentToLoad: SlackTestingModalComponent,
                                    payload: {
                                        data: (
                                            buttonRef: IButtonGeneratorInput,
                                            formGroup: FormGroup,
                                            modalId: Symbol
                                        ) => {
                                            if (formGroup.valid) {
                                                buttonRef.loader = true;
                                                const apiArgs =
                                                    Helper.generateHitApiConfig(
                                                        this.widgetRef
                                                            .widgetData
                                                            .widgetInfo.action
                                                    );
                                                const input = {
                                                    slackChannelIds: [
                                                        ...formGroup.value[
                                                            'users'
                                                        ],
                                                        ...formGroup.value[
                                                            'channels'
                                                        ]
                                                    ],
                                                    slackMessageInput:
                                                        formGroup.value[
                                                            'message'
                                                        ]
                                                };
                                                apiArgs.input = input;
                                                apiArgs.function = (
                                                    response
                                                ) => {
                                                    buttonRef.loader = false;
                                                    this.widgetRef.notificationsService.showSnackBar(
                                                        'Message sent successfully'
                                                    );
                                                    this.widgetRef.modalService.closeModal(
                                                        null,
                                                        modalId
                                                    );
                                                };
                                                apiArgs.errorFunction = (
                                                    error
                                                ) => {
                                                    buttonRef.loader = false;
                                                    this.widgetRef.notificationsService.showSnackBar(
                                                        'Error sending message',
                                                        true
                                                    );
                                                    this.widgetRef.modalService.closeModal(
                                                        null,
                                                        modalId
                                                    );
                                                };
                                                new HitApi(
                                                    apiArgs,
                                                    this.widgetRef.httpService,
                                                    this.widgetRef.ngZone
                                                ).hitApi();
                                            }
                                        }
                                    }
                                }
                            }
                        ],
                        modalWidthVw: 60,
                        modalHeightVh: 75,
                        noBottomPadding: true
                    };
                    this.widgetRef.modalService.openModal(modalArgs);
                }
            });

            // Adding Delete Button
            this.integrationInput.buttons.push({
                buttonName: 'Delete',
                buttonIcon: {
                    type: IconType.FONTAWSOME,
                    class: 'fas fa-trash'
                },
                buttonType: ButtonType.CIRCLE,
                buttonColorType: ButtonColorType.WARN,
                function: (modalId: Symbol) => {
                    // Delete integration
                    const args = Helper.generateHitApiConfig(
                        this.widgetRef.widgetData.widgetInfo.delete
                    );
                    args.input = {};
                    args.function = (response) => {
                        this.widgetRef.notificationsService.showSnackBar(
                            `Slack integration removed successfully`
                        );
                        this.widgetRef.refreshWidget();
                    };
                    args.errorFunction = (error) => {
                        this.widgetRef.notificationsService.showSnackBar(
                            error.error.message,
                            true
                        );
                        this.widgetRef.refreshWidget();
                    };
                    new HitApi(
                        args,
                        this.widgetRef.httpService,
                        this.widgetRef.ngZone
                    ).hitApi();
                },
                hoverText: 'Remove Slack',
                executeFunctionAfterConfirmation: {
                    modalName: 'Remove Slack Integration',
                    modalIcon: {
                        type: IconType.FONTAWSOME,
                        class: 'far fa-trash-alt'
                    },
                    contextIcon: {
                        extraClass: 'color-accent',
                        type: IconType.FONTAWSOME,
                        class: 'fas fa-exclamation-triangle'
                    },
                    confirmationMessage: `Are you sure you want to remove slack ?`,
                    buttonText: 'Remove',
                    buttonColorType: ButtonColorType.WARN,
                    loaderOnExec: true
                }
            });
        }
    }
    onIntegrate() {
        this.redirectionHandleService.widgetIdUsedForRedirection =
            this.widgetRef.widgetData.widgetId;
        const url = `https://slack.com/oauth/authorize?scope=bot&client_id=${GlobalConfiguration.SLACK_CLIENT_ID}&redirect_uri=${this.redirectionHandleService.redirectionUrl}&state=${IntegrationStates.SLACK_STATE}`;
        this.redirectionHandleService.redirectTo(
            this.widgetRef.widgetData.widgetId,
            url
        );
    }
    bindData(response: IIntegrationApiResponse): void {
        this.isConfigured = false;
        this.isIntegrated = response.integration;
        if (this.isIntegrated) {
            this.isConfigured = true;
        }
        this.integrationInput.buttons = [];
        this.generateButtons(response);
        this.widgetRef.endLoader();
    }

    ngAfterViewInit(): void {
        this.widgetRef.setBindData(this.bindData.bind(this));
    }
}
